import { useState, useRef } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { toast } from "sonner";
import { Upload, File, Image as ImageIcon, Download, Trash2, Eye } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { format } from "date-fns";

interface OrderFilesTabProps {
  orderId?: string;
}

interface OrderFile {
  id: string;
  order_id: string;
  file_name: string;
  file_path: string;
  file_type: string;
  file_size: number;
  uploaded_by: string | null;
  created_at: string;
  profiles?: {
    first_name: string | null;
    last_name: string | null;
  };
}

export function OrderFilesTab({ orderId }: OrderFilesTabProps) {
  const queryClient = useQueryClient();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [previewFile, setPreviewFile] = useState<{ url: string; name: string } | null>(null);
  const [uploadingFiles, setUploadingFiles] = useState<string[]>([]);

  // Fetch order files
  const { data: files = [], isLoading } = useQuery({
    queryKey: ["order-files", orderId],
    queryFn: async () => {
      if (!orderId) return [];
      const { data, error } = await supabase
        .from("order_files")
        .select("*")
        .eq("order_id", orderId)
        .is("deleted_at", null)
        .order("created_at", { ascending: false });
      
      if (error) throw error;

      // Batch fetch uploader profiles - eliminates N+1 queries
      const uploaderIds = [...new Set(
        data.map(file => file.uploaded_by).filter(Boolean)
      )] as string[];

      const profileMap: Record<string, { first_name: string | null; last_name: string | null }> = {};
      if (uploaderIds.length > 0) {
        const { data: profiles } = await supabase
          .from("profiles")
          .select("id, first_name, last_name")
          .in("id", uploaderIds);
        
        profiles?.forEach(profile => {
          profileMap[profile.id] = {
            first_name: profile.first_name,
            last_name: profile.last_name,
          };
        });
      }

      // Merge profile data into files
      const filesWithProfiles = data.map(file => ({
        ...file,
        profiles: file.uploaded_by ? profileMap[file.uploaded_by] || null : null,
      }));

      return filesWithProfiles as OrderFile[];
    },
    enabled: !!orderId,
  });

  // Upload mutation
  const uploadMutation = useMutation({
    mutationFn: async (file: File) => {
      if (!orderId) throw new Error("Order ID puuttuu");

      // Upload to storage
      const filePath = `${orderId}/${Date.now()}-${file.name}`;
      const { error: uploadError } = await supabase.storage
        .from("order-attachments")
        .upload(filePath, file);

      if (uploadError) throw uploadError;

      // Save metadata to database
      const { data: { user } } = await supabase.auth.getUser();
      const { error: dbError } = await supabase
        .from("order_files")
        .insert({
          order_id: orderId,
          file_name: file.name,
          file_path: filePath,
          file_type: file.type,
          file_size: file.size,
          uploaded_by: user?.id || null,
        });

      if (dbError) throw dbError;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["order-files", orderId] });
      toast.success("Tiedosto ladattu onnistuneesti");
    },
    onError: (error: Error) => {
      toast.error("Virhe tiedoston latauksessa", { description: error.message });
    },
  });

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: async (file: OrderFile) => {
      // Delete from storage
      const { error: storageError } = await supabase.storage
        .from("order-attachments")
        .remove([file.file_path]);

      if (storageError) throw storageError;

      // Soft delete from database
      const { error: dbError } = await supabase
        .from("order_files")
        .update({ deleted_at: new Date().toISOString() })
        .eq("id", file.id);

      if (dbError) throw dbError;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["order-files", orderId] });
      toast.success("Tiedosto poistettu");
    },
    onError: (error: Error) => {
      toast.error("Virhe tiedoston poistossa", { description: error.message });
    },
  });

  const handleFileSelect = async (files: FileList | null) => {
    if (!files || files.length === 0) return;

    const validFiles = Array.from(files).filter(file => {
      // Validate file size (max 20MB)
      if (file.size > 20 * 1024 * 1024) {
        toast.error("Tiedosto liian suuri", { description: `${file.name} on yli 20MB` });
        return false;
      }
      return true;
    });

    for (const file of validFiles) {
      setUploadingFiles(prev => [...prev, file.name]);
      await uploadMutation.mutateAsync(file);
      setUploadingFiles(prev => prev.filter(name => name !== file.name));
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = () => {
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    handleFileSelect(e.dataTransfer.files);
  };

  const downloadFile = async (file: OrderFile) => {
    try {
      const { data, error } = await supabase.storage
        .from("order-attachments")
        .createSignedUrl(file.file_path, 3600);

      if (error) throw error;
      
      window.open(data.signedUrl, "_blank");
    } catch (error) {
      toast.error("Virhe tiedoston latauksessa", { description: error instanceof Error ? error.message : "Tuntematon virhe" });
    }
  };

  const previewImage = async (file: OrderFile) => {
    if (!file.file_type.startsWith("image/")) return;

    try {
      const { data, error } = await supabase.storage
        .from("order-attachments")
        .createSignedUrl(file.file_path, 3600);

      if (error) throw error;
      
      setPreviewFile({ url: data.signedUrl, name: file.file_name });
    } catch (error) {
      toast.error("Virhe kuvan esikatselussa", { description: error instanceof Error ? error.message : "Tuntematon virhe" });
    }
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return bytes + " B";
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + " KB";
    return (bytes / (1024 * 1024)).toFixed(1) + " MB";
  };

  const getUploaderName = (file: OrderFile): string => {
    if (!file.profiles) return "Tuntematon";
    const { first_name, last_name } = file.profiles;
    if (first_name && last_name) return `${first_name} ${last_name}`;
    return "Tuntematon";
  };

  if (!orderId) {
    return (
      <div className="text-center py-8 text-muted-foreground">
        Tallenna tilaus ensin liittääksesi tiedostoja
      </div>
    );
  }

  return (
    <div className="space-y-4 mt-4">
      {/* Upload area */}
      <Card
        className={`border-2 border-dashed p-8 text-center cursor-pointer transition-colors ${
          isDragging ? "border-primary bg-primary/5" : "border-border hover:border-primary/50"
        }`}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onClick={() => fileInputRef.current?.click()}
      >
        <input
          ref={fileInputRef}
          type="file"
          multiple
          className="hidden"
          onChange={(e) => handleFileSelect(e.target.files)}
          accept="image/*,.pdf,.doc,.docx"
        />
        <Upload className="mx-auto h-12 w-12 text-muted-foreground mb-4" />
        <p className="text-sm text-muted-foreground mb-2">
          Vedä tiedostot tähän tai klikkaa valitaksesi
        </p>
        <p className="text-xs text-muted-foreground">
          Tuetut tiedostotyypit: Kuvat (JPG, PNG), PDF, DOC, DOCX (max 20MB)
        </p>
      </Card>

      {/* Uploading files */}
      {uploadingFiles.length > 0 && (
        <div className="space-y-2">
          {uploadingFiles.map((fileName) => (
            <Card key={fileName} className="p-3">
              <div className="flex items-center gap-3">
                <div className="animate-spin h-4 w-4 border-2 border-primary border-t-transparent rounded-full" />
                <span className="text-sm">{fileName}</span>
              </div>
            </Card>
          ))}
        </div>
      )}

      {/* Files list */}
      {isLoading ? (
        <div className="text-center py-8 text-muted-foreground">Ladataan tiedostoja...</div>
      ) : files.length === 0 ? (
        <div className="text-center py-8 text-muted-foreground">
          Ei tiedostoja. Lataa ensimmäinen tiedosto yllä.
        </div>
      ) : (
        <div className="space-y-2">
          {files.map((file) => {
            const isImage = file.file_type.startsWith("image/");
            return (
              <Card key={file.id} className="p-4">
                <div className="flex items-center gap-4">
                  <div className="flex-shrink-0">
                    {isImage ? (
                      <ImageIcon className="h-8 w-8 text-primary" />
                    ) : (
                      <File className="h-8 w-8 text-muted-foreground" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate">{file.file_name}</p>
                    <div className="flex items-center gap-3 text-xs text-muted-foreground mt-1">
                      <span>{formatFileSize(file.file_size)}</span>
                      <span>•</span>
                      <span>{format(new Date(file.created_at), "dd.MM.yyyy HH:mm")}</span>
                      <span>•</span>
                      <span>{getUploaderName(file)}</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {isImage && (
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        onClick={() => previewImage(file)}
                      >
                        <Eye className="h-4 w-4" />
                      </Button>
                    )}
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      onClick={() => downloadFile(file)}
                    >
                      <Download className="h-4 w-4" />
                    </Button>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      onClick={() => deleteMutation.mutate(file)}
                      disabled={deleteMutation.isPending}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </Card>
            );
          })}
        </div>
      )}

      {/* Image preview dialog */}
      <Dialog open={!!previewFile} onOpenChange={() => setPreviewFile(null)}>
        <DialogContent className="max-w-4xl">
          <DialogHeader>
            <DialogTitle>{previewFile?.name}</DialogTitle>
          </DialogHeader>
          {previewFile && (
            <div className="mt-4">
              <img
                src={previewFile.url}
                alt={previewFile.name}
                className="w-full h-auto rounded-lg"
              />
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
